import { DataSet } from './../../interfaces/DisplayInterfaces';
import {
  IBoxAndWhiskerDataPoints,
  IChartSeries,
  ITableData,
  IChartStatsCalc,
  IScatterDataPoints,
  IBoxPlotSeriesDefaultBoxToolTip,
  BoxPlotOptions
} from './highchart-platform-interfaces';
import { ChartStatsCalc } from './highchart-platform-models';
import * as _ from 'lodash';
import * as Highcharts from 'highcharts';
import { Descriptive } from './../../classes/Descriptive';
import * as moment from 'moment';
export type datapoint = Array<number | [number, number] | [string, number] | Highcharts.DataPoint>;

export class HighChartDataAdapter {

  // http://www.color-hex.com/color-palette/49175
  static colorsPalette = ['#a468a6', '#e67c52', '#e4a928', '#dec81c', '#86a67b', '#d39f99', '#cdd399', '#e5e5c9', '#5aaeae', '#93836b',
    '#a468a6', '#e67c52', '#e4a928', '#dec81c', '#86a67b', '#d39f99', '#cdd399', '#e5e5c9', '#5aaeae', '#93836b'];

  static createBoxplotSeries(srcData: DataSet,
    groupingColumn: string,
    targetValue: string,
    colorGroupingCol?: string,
    showPoints = false,
    seriesName?: string,
    roundToDecimalPlace = 3,
    customColumns?: string[],
    boxPlotOptions?: BoxPlotOptions): IChartSeries[] {
    let boxplotSeries: IChartSeries = <IChartSeries>{};
    let inLierSeries: IChartSeries = <IChartSeries>{};
    let outlierSeries: IChartSeries = <IChartSeries>{};
    let inLierDataArr = [];
    let outlierDataArr = [];
    let boxplotDataArr = [];
    let boxplotCategories = [];
    let colorCategories = [];
    let colorGroupedData = {};
    let colorGroupingColumnIndex = -1;
    let colors = [];
    // let extraProps = [];
    const groupingColumnIndex: number = srcData.columns.indexOf(groupingColumn);
    const targetValueIndex: number = srcData.columns.indexOf(targetValue);
    const seriesCollection: IChartSeries[] = [];
    let counter = 0;

    if (colorGroupingCol) {
      colorGroupingColumnIndex = srcData.columns.indexOf(colorGroupingCol);
      // grouping the data based on color
      colorGroupedData = this.groupData(srcData.data, colorGroupingColumnIndex);
      colorCategories = Object.keys(colorGroupedData).sort();
      _.forEach(colorCategories, (groupKey: string, index) => {
        let subGrp = this.groupData(colorGroupedData[groupKey], groupingColumnIndex);
        for (let prop in subGrp) {
          if (prop) {
            boxplotCategories.push('(' + groupKey + ')' + ' - ' + prop);
            counter++;
            const datapoints = [];
            const extraProps = [];
            // looping over array value corrosponding to each property
            // to build dataset based on groupingvalue
            _.forEach(subGrp[prop], (row: any) => {
              datapoints.push(Number(row[targetValueIndex]));
              extraProps.push(this.PushExtraPropertiesIfNeeded(customColumns, row, srcData.columns));
            });

            if (showPoints) {
              ColorHelper.h = this.colorsPalette[index];
              inLierDataArr = this.createInliers(datapoints, counter, inLierDataArr, ColorHelper.addOpacity(0.4), extraProps,
                boxPlotOptions ? boxPlotOptions.shapeInliers : undefined);
              outlierDataArr = this.createOutliers(datapoints, counter, outlierDataArr, ColorHelper.addOpacity(0.6), extraProps,
                boxPlotOptions ? boxPlotOptions.shapeOutliers : undefined);
            }
            boxplotDataArr.push(this.pushStats(datapoints, roundToDecimalPlace));
            colors.push(this.colorsPalette[index]);
          }
        }
        boxplotSeries.name = seriesName;
        boxplotSeries.data = boxplotDataArr;
        boxplotSeries.isGrouped = true;
        boxplotSeries.boxplotCategories = boxplotCategories;
        boxplotSeries.colors = colors;
        // tslint:disable-next-line:max-line-length
        HighChartDataAdapter.populateOutliersAndInliers(showPoints, inLierSeries, inLierDataArr, outlierSeries, outlierDataArr, colorGroupingCol, boxPlotOptions);
      });
    } else {
      const colorBuilder = {};
      const colorByColumnRequired = boxPlotOptions && boxPlotOptions.colorByColumn ? true : false;
      // If color by column is required with out group by.
      if (colorByColumnRequired) {
        const colorIndex = srcData.columns.indexOf(boxPlotOptions.colorByColumn);
        colorGroupedData = this.groupData(srcData.data, colorIndex);
        colorCategories = Object.keys(colorGroupedData);
        colorCategories.forEach((columnName, colIndex) => {
          colorBuilder[columnName] = this.colorsPalette[colIndex];
        });
      }
      const groupedData = this.groupData(srcData.data, groupingColumnIndex);
      // looping on each property of grouped data
      for (const prop in groupedData) {
        if (prop) {
          boxplotCategories.push(prop);
          counter++;
          const datapoints = [];
          const extraProps = [];
          let color = undefined;
          // looping over array value corrosponding to each property
          // to build dataset based on groupingvalue
          _.forEach(groupedData[prop], (row, rowIndex) => {
            datapoints.push(Number(row[targetValueIndex]));
            extraProps.push(this.PushExtraPropertiesIfNeeded(customColumns, row, srcData.columns));
            if (colorByColumnRequired && (groupedData[prop].length - 1 === rowIndex)) {
              color = row[srcData.columns.indexOf(boxPlotOptions.colorByColumn)];
            }
          });
          if (showPoints) {

            const inlineColor = (boxPlotOptions && boxPlotOptions.colorInliers) ? boxPlotOptions.colorInliers :
              colorByColumnRequired ? colorBuilder[color] : 'rgba(63, 191, 74, 0.2)';
            const outlineColor = (boxPlotOptions && boxPlotOptions.colorOutliers) ? boxPlotOptions.colorOutliers :
              colorByColumnRequired ? colorBuilder[color] : 'rgba(0, 0, 255, 0.5)';

            inLierDataArr = this.createInliers(datapoints, counter, inLierDataArr, inlineColor, extraProps,
              boxPlotOptions ? boxPlotOptions.shapeInliers : undefined);
            outlierDataArr = this.createOutliers(datapoints, counter, outlierDataArr, outlineColor, extraProps,
              boxPlotOptions ? boxPlotOptions.shapeOutliers : undefined);
          }
          boxplotDataArr.push(this.pushStats(datapoints, roundToDecimalPlace));
        }
      }
      boxplotSeries.name = seriesName;
      boxplotSeries.data = boxplotDataArr;
      boxplotSeries.isGrouped = false;
      boxplotSeries.boxplotCategories = boxplotCategories;
      boxplotSeries.tooltip = {};
      boxplotSeries.tooltip.headerFormat = '{point.key}<br/><span style="color:{series.color}">\u25CF</span> {series.name} <br/> ';
      boxplotSeries.tooltip.pointFormat = 'Maximum: <b>{point.high}</b><br/>' +
        'Upper quartile: <b>{point.q3}</b><br/>' + 'Median: <b>{point.median}</b><br/>' +
        'Lower quartile: <b>{point.q1}</b><br/>' + 'Minimum: <b>{point.low}</b><br/>';
      // tslint:disable-next-line:max-line-length
      HighChartDataAdapter.populateOutliersAndInliers(showPoints, inLierSeries, inLierDataArr, outlierSeries, outlierDataArr, colorGroupingCol, boxPlotOptions);
    }

    seriesCollection.push(boxplotSeries);
    if (showPoints) {
      seriesCollection.push(outlierSeries);
      seriesCollection.push(inLierSeries);
    }
    return seriesCollection;
  }

  // Handling extra properties for the custom tooltip
  private static PushExtraPropertiesIfNeeded(customColumns: string[], row: any, dataSetColumns: string[]): any[] {
    const tempArray = [];
    if (customColumns &&
      customColumns.length > 0) {
      customColumns.forEach(col => {
        tempArray[col] = row[dataSetColumns.indexOf(col)];
      });
    }
    return tempArray;
  }

  static AddScatterDataPoints(srcData: ITableData, x: string, y: string,
    isDateTime?: boolean, seriesName?: string, color?: string, addRL?: boolean, customData?: string[]): IChartSeries[] {
    // const scatterPlotChartSeries: IChartSeries = <IChartSeries>{};
    const seriesOptions = [];
    const regressionLine = <IChartSeries>{};
    const scatterPlotChartSeries = <IChartSeries>{};
    let lineData: datapoint;

    const xIndex: number = srcData.columns.indexOf(x);
    const yIndex: number = srcData.columns.indexOf(y);
    const scatterDataPoints = []; // Temp array that used to assign series data.


    _.forEach(srcData.data, (row: any) => {
      let datapoints = {};
      if (isDateTime) {
        datapoints['x'] = this.convertToUTCDate(row[xIndex]);
      } else {
        datapoints['x'] = Number(row[xIndex]);
      }
      datapoints['y'] = Number(row[yIndex]);
      if (customData && customData.length > 0) {
        _.forEach(customData, (dataName: string) => { datapoints[dataName] = row[srcData.columns.indexOf(dataName)]; })
      }
      scatterDataPoints.push(datapoints);
    })
    /**
     * If x_axis contains numbers and not DateTime and if The user wants to see Regression Line
     * Run this function that, prepares a data and series options for the line
     */
    if (!isDateTime && addRL) {
      lineData = this.findLineByLeastSquares(scatterDataPoints)
      regressionLine.type = 'line';
      regressionLine.name = 'Regression Line'
      regressionLine.data = lineData;
      regressionLine['lineWidth'] = 4;
      seriesOptions.push(regressionLine)
    }

    scatterPlotChartSeries.name = seriesName;
    scatterPlotChartSeries.color = color;
    scatterPlotChartSeries.data = scatterDataPoints;

    seriesOptions.push(scatterPlotChartSeries)
    return seriesOptions;
  }

  private static findLineByLeastSquares(scatterDataPoints): datapoint {
    let values_x = [], values_y = [];
    scatterDataPoints.forEach(points => {
      values_x.push(points['x']);
      values_y.push(points['y']);
    });

    let sum_x = 0, sum_y = 0, sum_xy = 0, sum_xx = 0, count = 0, x = 0, y = 0, values_length = values_x.length, linearRecurseData = [];
    if (values_x && values_y) {
      /*
     * Calculates the sum for each of the parts necessary.
     */
      for (let v = 0; v < values_length; v++) {
        x = values_x[v];
        y = values_y[v];
        sum_x += x;
        sum_y += y;
        sum_xx += x * x;
        sum_xy += x * y;
        count++;
      }
      /*
       * Calculate m and b for the formular:y = x * m + b
       */
      let m = (count * sum_xy - sum_x * sum_y) / (count * sum_xx - sum_x * sum_x),
        b = (sum_y / count) - (m * sum_x) / count;

      /*
       * Makes the x and y result line
       */
      let result_values_x = [], result_values_y = [];

      for (let v = 0; v < values_length; v++) {
        let innerTemp = [];
        innerTemp[0] = values_x[v];
        innerTemp[1] = values_x[v] * m + b;
        linearRecurseData.push(innerTemp);
      }
      let sortedData;
      sortedData = _.sortBy(linearRecurseData, (row: number[]) => row[0])
      linearRecurseData = sortedData;
    }
    return linearRecurseData;
  }


  private static pushStats(datapoints: any[], roundToDecimalPlace): IBoxPlotSeriesDefaultBoxToolTip {
    let statCalc: IChartStatsCalc = new ChartStatsCalc(datapoints);
    let statPoints = <IBoxPlotSeriesDefaultBoxToolTip>{};
    statPoints['low'] = Descriptive.roundTo(statCalc.getWhiskerLow(), roundToDecimalPlace);
    statPoints['q1'] = Descriptive.roundTo(statCalc.getLowerQuantile(), roundToDecimalPlace);
    statPoints['median'] = Descriptive.roundTo(statCalc.getMedian(), roundToDecimalPlace);
    statPoints['q3'] = Descriptive.roundTo(statCalc.getUpperQuantile(), roundToDecimalPlace);
    statPoints['high'] = Descriptive.roundTo(statCalc.getWhiskerHigh(), roundToDecimalPlace);
    return statPoints;
  }

  private static getRandomNum(num: number, index: number) {
    if (index % 2 === 0) {
      return num + Math.random() * .1;
    } else {
      return num - Math.random() * .1;
    }
  }

  private static convertToUTCDate(date: string) {
    if (date) {
      return moment.utc(date).valueOf();
    }
  }

  private static createInliers(
    datapoints: any,
    counter: number,
    inliers: Array<Number>,
    color: string,
    extraProps?: any[],
    shape?: string): any {
    let filteredInliers;
    let statCalc: IChartStatsCalc = new ChartStatsCalc(datapoints);
    const whiskerLow = statCalc.getWhiskerLow();
    const whiskerHigh = statCalc.getWhiskerHigh();
    const extraPropsInliers = [];
    filteredInliers = datapoints.filter((x, i) => {
      if (x >= whiskerLow && x <= whiskerHigh) {
        extraPropsInliers.push(extraProps[i]);
        return true;
      }
    });
    HighChartDataAdapter.processDataPoints(filteredInliers, counter, inliers, color, true, extraPropsInliers, shape);
    return inliers;
  }

  private static createOutliers(datapoints: any,
    counter: number,
    outliers: Array<Number>,
    color: string,
    extraProps: any[],
    shape?: string): any {
    let filteredOutliers;
    let statCalc: IChartStatsCalc = new ChartStatsCalc(datapoints);
    const whiskerLow = statCalc.getWhiskerLow();
    const whiskerHigh = statCalc.getWhiskerHigh();
    const extraPropsOutliers = [];
    filteredOutliers = datapoints.filter((x, i) => {
      if (x < whiskerLow || x > whiskerHigh) {
        extraPropsOutliers.push(extraProps[i])
        return true;
      }
    });
    HighChartDataAdapter.processDataPoints(filteredOutliers, counter, outliers, color, false, extraPropsOutliers, shape);
    return outliers;
  }


  private static processDataPoints(datapoints: any,
    counter: number,
    scatterDataPoints: any[],
    color: string,
    jiggered = true,
    extraProps?: any[],
    shape?: string) {
    _.forEach(datapoints, (datapoint: number, index: number) => {
      const scatterDataPointsTemp = {};
      scatterDataPointsTemp['x'] = (jiggered) ? this.getRandomNum(counter - 1, index) : counter - 1;
      scatterDataPointsTemp['y'] = Number(datapoint);
      scatterDataPointsTemp['color'] = color;
      if (shape) {
        scatterDataPointsTemp['marker'] = { symbol: shape };
      }
      if (extraProps && extraProps.length > 0) {
        Object.keys(extraProps[index]).forEach(key => {
          scatterDataPointsTemp[key] = extraProps[index][key];
        });
      }

      scatterDataPoints.push(scatterDataPointsTemp);
    });
  }

  private static populateOutliersAndInliers(showPoints: boolean,
    inLierSeries: IChartSeries,
    inLierDataArr: any[],
    outlierSeries: IChartSeries,
    outlierDataArr: any[],
    colorGroupingCol: string,
    boxPlotOptions?: BoxPlotOptions) {
    if (showPoints) {
      // inliers
      inLierSeries.data = inLierDataArr;
      inLierSeries.name = 'inliers';
      inLierSeries.type = 'scatter';
      if (boxPlotOptions && boxPlotOptions.shapeInliers) {
        inLierSeries.marker = { symbol: boxPlotOptions.shapeInliers };
      }
      if (boxPlotOptions && boxPlotOptions.colorInliers) {
        inLierSeries.color = boxPlotOptions.colorInliers;
      }
      // outliers
      outlierSeries.data = outlierDataArr;
      outlierSeries.name = 'outliers';
      outlierSeries.type = 'scatter';
      if (boxPlotOptions && boxPlotOptions.shapeOutliers) {
        outlierSeries.marker = { symbol: boxPlotOptions.shapeOutliers };
      }
      if (boxPlotOptions && boxPlotOptions.colorOutliers) {
        outlierSeries.color = boxPlotOptions.colorOutliers;
      }

    }
  }

  private static groupData(data, index) {
    const sgb = _.groupBy(data, (row) => {
      return row[index];
    });
    return sgb;
  }
}
export class ColorHelper {
  static h = '';
  static hexToR(h) { return parseInt((ColorHelper.cutHex(h)).substring(0, 2), 16) }
  static hexToG(h) { return parseInt((ColorHelper.cutHex(h)).substring(2, 4), 16) }
  static hexToB(h) { return parseInt((ColorHelper.cutHex(h)).substring(4, 6), 16) }
  static cutHex(h) { return (h.charAt(0) === '#') ? h.substring(1, 7) : h };

  static addOpacity(opacity) {
    return `rgba(${this.hexToR(this.h)}, ${this.hexToG(this.h)}, ${this.hexToB(this.h)}, ${opacity})`;
  }
}
